import React from "react";
import { toast } from "react-toastify";
import { Button, Modal } from "@/Components/Common/Tailwind";
import { SITE_URL } from "../../api";
import GiftPhoneNumList from "../../pages/UserPage/purchaseList/GiftPhoneNumList.jsx";
import { shareKakao } from "../../common/social/kakao/api";
import { packageService } from "../../api/services/package.service";
import { handleCopyClipBoard } from "../../common/utils/clipboard";
import {
  buildShareUrl,
  getGiftSendErrorMessage,
  getRequiredResponseValue,
} from "../../common/social/kakao/giftShare.mjs";

type ReservationGiftInfo = {
  id: string | number;
  name: string;
  reservationDate: string;
  reservationEndDate?: string;
  spot: string | number;
  amount: number;
  state: string;
  image?: string;
  gift?: {
    id?: string | number;
  };
};

interface ReservationGiftModalProps {
  info: ReservationGiftInfo;
  setInfo: (value: Partial<ReservationGiftInfo>) => void;
  visible: boolean;
  setVisible: (value: boolean) => void;
  needRefresh: () => void;
}

const ReservationGiftModal: React.FC<ReservationGiftModalProps> = ({
  info,
  setInfo,
  visible,
  setVisible,
  needRefresh,
}) => {
  const togModal = () => setVisible(!visible);
  const [giftShareUrl, setGiftShareUrl] = React.useState("");
  const [isSharing, setIsSharing] = React.useState(false);
  const existingGiftShareUrl = info?.gift?.id
    ? buildShareUrl(SITE_URL, ["gift", "reservation", info.gift.id])
    : "";
  const displayedGiftShareUrl = giftShareUrl || existingGiftShareUrl;

  React.useEffect(() => {
    if (!visible) {
      setGiftShareUrl("");
      setIsSharing(false);
    }
  }, [visible, info?.id]);

  const handleGiftUrlShare = async () => {
    if (isSharing) return;

    setIsSharing(true);
    try {
      let shareUrl = displayedGiftShareUrl;

      if (!shareUrl) {
        const data = await packageService.giftReservationByUrl(Number(info.id));
        const giftId = getRequiredResponseValue(data, "gift_id");
        shareUrl = buildShareUrl(SITE_URL, ["gift", "reservation", giftId]);
        setGiftShareUrl(shareUrl);
        needRefresh();
      }

      const opened = shareKakao({
        title: `${info.name}`,
        description: "예약상품을 선물 받았습니다.",
        imageUrl: info.image ? info.image : "",
        shareUrl,
      });

      if (opened) {
        toast.success("카카오톡 공유창을 열었습니다.");
        setInfo({});
        setVisible(false);
        return;
      }

      await handleCopyClipBoard(shareUrl, {
        successMessage: "카카오톡 공유를 열 수 없어 링크를 복사했어요.",
        errorMessage: "카카오톡 공유를 열 수 없습니다. 링크를 복사해 주세요.",
      });
    } catch (error) {
      console.error(error);
      toast.error(getGiftSendErrorMessage(error, "선물하기에 실패했습니다."));
    } finally {
      setIsSharing(false);
    }
  };

  if (!Object.keys(info).length) return null;

  return (
    <React.Fragment>
      <Modal toggle={togModal} isOpen={visible} centered>
        <div>
          <div className="mb-4 flex justify-center">
            <h4 className="text-lg font-semibold">예약티켓 선물하기</h4>
          </div>
          <ul className="list-group">
            <li className="list-group-item">예약ID : {info.id}</li>
            <li className="list-group-item">예약상품 : {info.name}</li>

            {info.reservationEndDate && info.reservationDate === info.reservationEndDate ?
              <li className="list-group-item">예약일 : {info.reservationDate}</li> :
              <li className="list-group-item">예약일 : {info.reservationDate} ~ {info.reservationEndDate}</li>}

            <li className="list-group-item">자리 : {info.spot}</li>
            <li className="list-group-item">결제금액 : {info.amount}</li>
            <li className="list-group-item">상태 : {info.state}</li>
          </ul>

          <div style={{ marginTop: "24px" }}></div>
          <div className="flex justify-center mx-1 mb-4">
            <Button
              variant="primary"
              className="gap-2 btn-kakao"
              disabled={isSharing}
              onClick={handleGiftUrlShare}
            >
              <i className="ri-gift-fill"></i>
              {isSharing ? "공유 준비중..." : "카카오톡으로 선물보내기"}
            </Button>
          </div>

          {displayedGiftShareUrl ? (
            <div className="mx-1 mb-4">
              <label className="mb-1 block text-sm text-gray-500" htmlFor="reservationGiftShareUrl">
                공유 링크
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="reservationGiftShareUrl"
                  readOnly
                  value={displayedGiftShareUrl}
                  className="h-9 min-w-0 flex-1 rounded border border-gray-300 px-2 text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSharing}
                  onClick={() => handleCopyClipBoard(displayedGiftShareUrl)}
                >
                  복사
                </Button>
              </div>
            </div>
          ) : null}

          <div style={{ marginTop: "24px" }}></div>
          <GiftPhoneNumList sendItem={packageService.inviteUserForReservation} {...{ info, setInfo, visible, setVisible, needRefresh }} />
        </div>
      </Modal>
    </React.Fragment>
  );
};
export default ReservationGiftModal;
